import { Children, RefObject, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { InView } from 'react-intersection-observer';
import { cva, cx } from 'class-variance-authority';
import * as AspectRatio from '@radix-ui/react-aspect-ratio';

import { Box, Button } from '@oresundsbron/bridge-ui';
import { ArrowRight } from '@oresundsbron/icons';

const indicator = cva(
  [
    'h-2',
    'rounded-full',
    'sm:h-3',
    'transition-all',
    'duration-1500',
    'ease-in-out',
  ],
  {
    variants: {
      active: {
        true: ['bg-primary-700', 'w-4', 'sm:w-8'],
        false: ['bg-primary-300', 'w-2', 'sm:w-3'],
      },
    },
  }
);

const button = cva(
  ['absolute', 'top-0', 'bottom-0', 'flex', 'items-center', 'p-0'],
  {
    variants: {
      pos: {
        left: ['-left-7', 'justify-start'],
        right: ['-right-7', , 'justify-end'],
      },
    },
  }
);

export const CarouselV2 = ({
  children,
  className,
}: {
  className?: string;
  children: JSX.Element[];
}) => {
  const { t } = useTranslation(['common']);
  const ref = useRef<HTMLDivElement>();
  const items = useMemo(() => Children.toArray(children), [children]);
  const [showing, setShowing] = useState(0);

  const next = () => {
    const el = ref.current;
    if (el) {
      const slide = showing + 1;
      el.scrollLeft = el.offsetWidth * slide;
      setShowing(slide);
    }
  };

  const prev = () => {
    const el = ref.current;
    if (el) {
      const slide = showing - 1;
      el.scrollLeft = el.offsetWidth * slide;

      setShowing(slide);
    }
  };

  const NavigationalButtons = () => (
    <>
      <Box
        className={button({
          pos: 'left',
        })}
      >
        <Button
          className={cx(
            'h-12 w-12 rotate-180 rounded-full sm:h-16 sm:w-16',
            showing === 0 ? 'hidden' : ''
          )}
          startIcon={<ArrowRight />}
          onClick={prev}
          iconSize="lg"
          intent="carousel"
          aria-label={t('pagination.previous').toString()}
        />
      </Box>

      <Box
        className={button({
          pos: 'right',
        })}
      >
        <Button
          className={cx(
            'h-12 w-12 rounded-full sm:h-16 sm:w-16',
            showing === items.length - 1 ? 'hidden' : ''
          )}
          startIcon={<ArrowRight />}
          onClick={next}
          intent="carousel"
          iconSize="lg"
          aria-label={t('pagination.next').toString()}
        />
      </Box>
    </>
  );

  const CarouselPagination = () => (
    <Box
      className={cx([
        'absolute',
        'bottom-2',
        'left-0',
        'right-0',
        'm-auto',
        'flex',
        'max-w-max',
        'justify-center',
        'gap-2',
        'rounded-2xl',
        'bg-white',
        'px-2',
        'py-2',
        'sm:bottom-3',
        'sm:px-3',
      ])}
    >
      {items.map((_, i) => (
        <Box
          key={i}
          className={cx(
            ' transition-all ease-in-out',
            indicator({ active: i === showing }),
            i === showing ? 'w-6' : 'w-2'
          )}
        />
      ))}
    </Box>
  );

  return (
    <Box className={cx('grid', 'grid-cols-1', 'gap-4', 'relative', className)}>
      <Box className="relative">
        <AspectRatio.Root
          className={cx([
            'flex',
            'snap-x',
            'snap-mandatory',
            'flex-nowrap',
            'overflow-hidden',
            'scroll-smooth',
            'rounded-xl',
            '[@media(pointer:coarse)]:overflow-scroll',
          ])}
          ref={ref as RefObject<HTMLDivElement>}
          ratio={16 / 9}
        >
          {items.map((child, i) => (
            <InView
              key={i}
              onChange={(inView) => {
                inView && setShowing(i);
              }}
              trackVisibility
              threshold={1}
              delay={300}
            >
              {({ ref }) => (
                <Box
                  ref={ref}
                  className="relative h-full w-full flex-[0_0_auto] snap-start"
                >
                  {child}
                </Box>
              )}
            </InView>
          ))}
        </AspectRatio.Root>

        <NavigationalButtons />

        <CarouselPagination />
      </Box>
    </Box>
  );
};
